import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { parseISO } from "date-fns";
import { addLocale } from "primereact/api";
import { Col, ModalBody, Row } from "react-bootstrap";

import { axiosPostRequest, axiosPutRequest } from "../../api/ApiClient";
import DialogMessage from "../../components/DialogMessage";
import { ptBrDatepickerTranslations } from "../../i18n/locales/pt-br";
import VigenciaPlanoAtividadeDTO from "../../model/dto/VigenciaPlanoAtividadeDTO";
import IModalProps from "../../model/ModalProps";
import ButtonStyles from "../../styles/components/ButtonStyles";
import CheckRadioStyles from "../../styles/components/CheckRadioStyles";
import DialogStyles from "../../styles/components/DialogStyles";
import InputCalendarStyles from "../../styles/components/InputCalendarStyles";
import ModalStyles from "../../styles/components/ModalStyles";
import { MASK_DATE } from "../../utils/constants";
import GenericUtils from "../../utils/GenericUtils";
import StringUtils from "../../utils/types/StringUtils";

addLocale("pt-BR", ptBrDatepickerTranslations);

interface IModalValidityPeriodProps extends IModalProps {
    params: any;
}

function ModalValidityPeriod(props: IModalValidityPeriodProps) {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const translate = {
        validityPeriod: StringUtils.translate("validityPeriod"),
        operationFailure: StringUtils.translate("operationFailure"),
        validityPeriodSuccses: StringUtils.translate("validityPeriodSuccses"),
        setPeriod: StringUtils.translate("setPeriod"),
    };

    const [semester, setSemester] = useState("");
    const [period, setPeriod] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const periodRef = useRef<any>();
    const isFormValid =
        semester && period && Boolean(period[0]) && Boolean(period[1]);

    async function createValidityPeriod(dto: VigenciaPlanoAtividadeDTO) {
        return axiosPostRequest(
            "/docente/v1/vigencias-plano-atividade",
            dto,
            acessToken,
            instance
        );
    }

    async function updateValidityPeriod(dto: VigenciaPlanoAtividadeDTO) {
        return axiosPutRequest(
            `/docente/v1/vigencias-plano-atividade/${String(
                dto.planoAtividadeId
            )}`,
            dto,
            acessToken,
            instance
        );
    }

    const handleSubmit = async () => {
        if (isFormValid) {
            const dto: VigenciaPlanoAtividadeDTO = {
                planoAtividadeId: props.params?.id || null,
                ano: period[0]?.getFullYear(),
                semestre: Number(semester),
                planoAtividadeDtInicio: StringUtils.formatDateString(
                    period[0]?.toISOString(),
                    MASK_DATE
                ),
                planoAtividadeDtFim: StringUtils.formatDateString(
                    period[1]?.toISOString(),
                    MASK_DATE
                ),
            };

            const response = props.params?.id
                ? await updateValidityPeriod(dto)
                : await createValidityPeriod(dto);

            if (response.status >= 200 && response.status < 300) {
                setSuccessMessage(translate.validityPeriodSuccses);
            } else {
                setErrorMessage(
                    response.data?.details || translate.operationFailure
                );
            }
        }
    };

    useEffect(() => {
        if (props.open) {
            if (props.params) {
                setSemester(String(props.params.semestre));
                setPeriod([
                    parseISO(props.params.dtInicio),
                    parseISO(props.params.dtFim),
                ]);
            } else {
                setSemester("");
                setPeriod(null);
            }
        }
    }, [props.open]);

    useEffect(() => {
        if (
            periodRef?.current &&
            period &&
            Boolean(period[0]) &&
            Boolean(period[1])
        ) {
            periodRef?.current?.hide();
        }
    }, [period]);

    const handleCloseDialogSuccess = () => {
        setSuccessMessage("");
        if (props.onClose) props.onClose({ success: true });
    };

    const DialogSuccess = (
        <DialogMessage
            type="success"
            show={Boolean(successMessage)}
            title={successMessage}
            onClose={handleCloseDialogSuccess}
        />
    );

    const handleCloseDialogError = () => {
        setErrorMessage("");
    };

    const DialogError = (
        <DialogMessage
            type="error"
            show={Boolean(errorMessage)}
            title={errorMessage}
            onClose={handleCloseDialogError}
        />
    );

    const today = new Date();

    return (
        <DialogStyles.StyledModal
            show={props.open}
            onHide={props.onClose}
            size="sm"
            centered
            backdrop="static"
            keyboard={false}
            aria-labelledby="contained-modal-title-vcenter"
        >
            <DialogStyles.StyledModalHeader closeButton />
            <ModalBody>
                <ModalStyles.StyledModalTitle>
                    {translate.validityPeriod}
                </ModalStyles.StyledModalTitle>
                <Row className="mt-3">
                    <Col>
                        <div key="inline" className="mb-3">
                            <CheckRadioStyles.StyledFormCheck
                                inline
                                label="1º semestre"
                                name="group1"
                                type="radio"
                                id="inline-1"
                                checked={semester === "1"}
                                onChange={() => setSemester("1")}
                            />
                            <CheckRadioStyles.StyledFormCheck
                                inline
                                label="2º semestre"
                                name="group1"
                                type="radio"
                                id="inline-2"
                                checked={semester === "2"}
                                onChange={() => setSemester("2")}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <InputCalendarStyles.StyledCalendar
                            ref={periodRef}
                            placeholder={translate.setPeriod}
                            dateFormat="dd/mm/yy"
                            locale="pt-BR"
                            selectionMode="range"
                            showButtonBar
                            readOnlyInput
                            minDate={today}
                            autoZIndex
                            touchUI
                            value={period}
                            onChange={(e: any) => setPeriod(e.target?.value)}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <DialogStyles.StyledModalFooter>
                <ButtonStyles.BtnPrimary
                    disabled={!isFormValid}
                    onClick={() => handleSubmit()}
                    className="mx-auto"
                >
                    Ok
                </ButtonStyles.BtnPrimary>
            </DialogStyles.StyledModalFooter>
            {successMessage ? DialogSuccess : null}
            {errorMessage ? DialogError : null}
        </DialogStyles.StyledModal>
    );
}

export default ModalValidityPeriod;
